import * as React from 'react';
import {
  Alert,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Clock as ClockIcon,
  HouseSimple as HouseSimpleIcon,
  WarningCircle as WarningCircleIcon,
  XCircle as XCircleIcon,
} from '@phosphor-icons/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import type { Request } from '@/types/v2/request';
import axios from '@/lib/v2/axios';
import { useCommunityRole } from '@/hooks/v2/use-community-role';
import { SectionLabel } from './request-details-card';

dayjs.extend(relativeTime);

interface ArcReviewSectionProps {
  request: Request;
}

export function ArcReviewSection({ request }: ArcReviewSectionProps): React.JSX.Element {
  const { hasBoardLevelAccess } = useCommunityRole();
  const queryClient = useQueryClient();
  const [conditions, setConditions] = React.useState(request.conditions ?? '');
  const [isDirty, setIsDirty] = React.useState(false);

  React.useEffect(() => {
    setConditions(request.conditions ?? '');
    setIsDirty(false);
  }, [request.conditions]);

  const saveConditionsMutation = useMutation({
    mutationFn: async (value: string) => {
      await axios.put(`/api/v2/communities/${request.community_id}/requests/${request.id}`, {
        conditions: value,
      });
    },
    onSuccess: () => {
      toast.success('Conditions saved');
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ['request'] });
    },
    onError: () => {
      toast.error('Failed to save conditions');
    },
  });

  const dueDate = request.due_date ? dayjs(request.due_date) : null;
  const now = dayjs();
  const daysRemaining = dueDate ? dueDate.diff(now, 'day') : null;
  const isOverdue = daysRemaining !== null && daysRemaining < 0;
  const isUrgent = daysRemaining !== null && daysRemaining >= 0 && daysRemaining <= 7;
  const dueDateColor = isOverdue ? 'error' : isUrgent ? 'warning' : 'success';
  const dueDateLabel = isOverdue
    ? `Overdue by ${Math.abs(daysRemaining!)} days`
    : daysRemaining === 0
    ? 'Due today'
    : daysRemaining !== null
    ? `${daysRemaining} days remaining`
    : null;

  const isApprovedWithConditions = request.request_status?.name === 'approved_with_conditions';

  return (
    <Box>
      <SectionLabel icon={<HouseSimpleIcon size={14} />}>ARC Review</SectionLabel>

      <Stack spacing={2.5} sx={{ mt: 2 }}>
        {/* Deadline */}
        {dueDate && (
          <Stack spacing={0.75}>
            <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Response Deadline
            </Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <ClockIcon size={14} />
              <Typography sx={{ fontSize: '0.875rem' }}>{dueDate.format('MMMM D, YYYY')}</Typography>
              {dueDateLabel && (
                <Chip
                  color={dueDateColor}
                  label={dueDateLabel}
                  size="small"
                  variant="outlined"
                  icon={isOverdue ? <WarningCircleIcon size={12} /> : undefined}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Stack>
            {isOverdue && (
              <Alert severity="error" sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                This ARC request has exceeded the response deadline.
              </Alert>
            )}
            {isUrgent && !isOverdue && (
              <Alert severity="warning" sx={{ mt: 0.5, py: 0, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
                Due in {daysRemaining} day{daysRemaining === 1 ? '' : 's'} — prompt review required.
              </Alert>
            )}
          </Stack>
        )}

        {/* Review outcomes legend */}
        <Stack spacing={0.75}>
          <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Review Outcomes
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip icon={<CheckCircleIcon size={12} />} label="Approved" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }} color="success" />
            <Chip icon={<CheckCircleIcon size={12} />} label="With Conditions" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }} color="success" />
            <Chip icon={<XCircleIcon size={12} />} label="Rejected" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }} color="error" />
            <Chip label="Needs Info" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600 }} color="secondary" />
          </Stack>
        </Stack>

        {/* Conditions */}
        {(isApprovedWithConditions || request.conditions) && (
          <Stack spacing={0.75}>
            <Typography color="text.disabled" sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Approval Conditions
            </Typography>
            {hasBoardLevelAccess ? (
              <Stack spacing={1}>
                <TextField
                  disabled={saveConditionsMutation.isPending}
                  fullWidth
                  minRows={2}
                  maxRows={6}
                  multiline
                  onChange={(e) => {
                    setConditions(e.target.value);
                    setIsDirty(e.target.value !== (request.conditions ?? ''));
                  }}
                  placeholder="Conditions the homeowner must satisfy..."
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' } }}
                  value={conditions}
                />
                {isDirty && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Chip
                      clickable
                      color="primary"
                      label={saveConditionsMutation.isPending ? 'Saving...' : 'Save'}
                      onClick={() => { saveConditionsMutation.mutate(conditions); }}
                      size="small"
                      sx={{ height: 24, fontSize: '0.75rem' }}
                    />
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: '0.8125rem' }} variant="body2">
                {request.conditions || 'No conditions specified.'}
              </Typography>
            )}
          </Stack>
        )}

        {/* Resident info */}
        {!hasBoardLevelAccess && (
          <Typography color="text.secondary" sx={{ fontSize: '0.8125rem', lineHeight: 1.6 }} variant="body2">
            ARC requests are reviewed by the Architectural Review Committee. You will be notified of the decision within the response deadline.
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
